import { useMemo, useState } from "react";
import { type Property } from "@/types/property";
import type { PropertyContract } from "@/hooks/useContracts";

type Filters = {
  propertyIds: string[];
  organizationIds: string[];
};

interface UseFilteredPropertiesProps {
  properties: Property[];
  contracts: PropertyContract[];
}

const useFilteredProperties = ({ properties, contracts }: UseFilteredPropertiesProps) => {
  const [filters, setFilters] = useState<Filters>({
    propertyIds: [],
    organizationIds: [],
  });

  const handleFilterChange = <K extends keyof Filters>(
    key: K,
    value: Filters[K],
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const filteredProperties = useMemo(() => {
    // First, filter out properties hidden from calendar
    let result = properties.filter(p => !p.hiddenFromCalendar);

    // Filter by organizations
    if (filters.organizationIds.length > 0) {
      const activeContracts = contracts.filter((c) => c.status === "active");
      const propertyIdsInOrgs = new Set(
        activeContracts
          .filter((c) => c.organization_id && filters.organizationIds.includes(c.organization_id))
          .map((c) => c.property_id)
      );
      result = result.filter((p) => propertyIdsInOrgs.has(p.id));
    }

    // Filter by specific properties
    if (filters.propertyIds.length > 0) {
      result = result.filter((p) => filters.propertyIds.includes(p.id));
    }

    // Sort by sortOrder (ascending, nulls/999 last)
    result.sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));

    return result;
  }, [properties, contracts, filters]);

  return {
    filters,
    handleFilterChange,
    filteredProperties,
  };
};

export default useFilteredProperties;
